import React, { useState, useMemo } from 'react';
import { StockSelectionBlock, TickerLine, PerformancePeriod } from '../../types/performance';
import StrategyBadge from '../common/StrategyBadge';
import { fmtUsd, fmtPct, pnlClass, fmtExpiry } from '../../utils/performanceFormat';

type SortColumn = 'symbol' | 'stock_pnl' | 'option_pnl' | 'dividends' | 'total_pnl' | 'return';
type SortDirection = 'asc' | 'desc';

interface PositionPnLTableProps {
  data: StockSelectionBlock | null;
  selectedPeriod: PerformancePeriod;
}

/**
 * Stock Selection: each ticker's share of the period (sums of its legs'
 * gold.position_daily rows; return on capital = P&L / average capital
 * committed, computed by the backend). The footer is the period's KPI row.
 */
const PositionPnLTable: React.FC<PositionPnLTableProps> = ({ data, selectedPeriod }) => {
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({ column: 'total_pnl', direction: 'desc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol); else next.add(symbol);
      return next;
    });
  };

  const rows = useMemo(() => {
    const list = [...(data?.tickers || [])];
    list.sort((a, b) => {
      if (sort.column === 'symbol') {
        return sort.direction === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
      }
      const av = a[sort.column as keyof TickerLine] as number | null;
      const bv = b[sort.column as keyof TickerLine] as number | null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sort.direction === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [data, sort]);

  const handleSort = (column: SortColumn) => {
    setSort(prev => ({ column, direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc' }));
  };
  const indicator = (column: SortColumn) => (sort.column !== column ? '⇅' : sort.direction === 'asc' ? '↑' : '↓');

  if (!data || rows.length === 0) {
    return (
      <div className="position-pnl-table-empty">
        <p>No positions were active during the selected period ({selectedPeriod})</p>
      </div>
    );
  }
  const t = data.totals;

  return (
    <div className="position-pnl-table-container">
      <table className="position-pnl-table">
        <thead>
          <tr>
            <th className="sortable-header" onClick={() => handleSort('symbol')}>Ticker {indicator('symbol')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('stock_pnl')}>Stock P&L {indicator('stock_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('option_pnl')}>Option P&L {indicator('option_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('dividends')}>Dividend P&L {indicator('dividends')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('total_pnl')}>Total P&L {indicator('total_pnl')}</th>
            <th className="sortable-header numeric-column" onClick={() => handleSort('return')} title="P&L divided by the average capital committed (return on capital)">Return {indicator('return')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <React.Fragment key={row.symbol}>
              <tr className={`ticker-row ${expanded.has(row.symbol) ? 'expanded' : ''}`} onClick={(e) => toggle(row.symbol, e)}>
                <td className="ticker-cell">
                  <span className="expand-icon">{expanded.has(row.symbol) ? '▼' : '▶'}</span>
                  {row.symbol}
                  {row.legs.length > 1 && <span className="contract-count">({row.legs.length})</span>}
                </td>
                <td className={`numeric-cell ${pnlClass(row.stock_pnl)}`}>{fmtUsd(row.stock_pnl)}</td>
                <td className={`numeric-cell ${pnlClass(row.option_pnl)}`}>{fmtUsd(row.option_pnl)}</td>
                <td className={`numeric-cell ${pnlClass(row.dividends)}`}>{fmtUsd(row.dividends)}</td>
                <td className={`numeric-cell ${pnlClass(row.total_pnl)}`}>{fmtUsd(row.total_pnl)}</td>
                <td className={`numeric-cell ${pnlClass(row.return)}`}>{fmtPct(row.return)}</td>
              </tr>
              {expanded.has(row.symbol) && row.legs.map(leg => (
                <tr key={leg.position_id} className="contract-row">
                  <td className="contract-cell">
                    <span className="contract-label">
                      {(leg.contracts ?? 1) > 1 && `${leg.contracts}x • `}Strike {leg.strike != null ? `$${leg.strike.toFixed(2)}` : '—'} • Exp {fmtExpiry(leg.expiry)}
                    </span>
                    <StrategyBadge positionType={leg.position_type} compact />
                    {leg.status && leg.status !== 'active' && <span className={`status-badge status-${leg.status}`}>{leg.status}</span>}
                  </td>
                  <td className={`numeric-cell ${pnlClass(leg.stock_pnl)}`}>{fmtUsd(leg.stock_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(leg.option_pnl)}`}>{fmtUsd(leg.option_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(leg.dividends)}`}>{fmtUsd(leg.dividends)}</td>
                  <td className={`numeric-cell ${pnlClass(leg.total_pnl)}`}>{fmtUsd(leg.total_pnl)}</td>
                  <td className={`numeric-cell ${pnlClass(leg.return)}`}>{fmtPct(leg.return)}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr className="summary-row">
            <td className="summary-label">Total ({rows.length} tickers)</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.stock_pnl)}`}>{fmtUsd(t.stock_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.option_pnl)}`}>{fmtUsd(t.option_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.dividends)}`}>{fmtUsd(t.dividends)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.total_pnl)}`}>{fmtUsd(t.total_pnl)}</td>
            <td className={`numeric-cell summary-value ${pnlClass(t.return)}`}>{fmtPct(t.return)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default PositionPnLTable;
